import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';

/**
 * Standard Object Keys supported in Oblique
 */
export const PERMISSION_OBJECTS = {
    DEALS: 'deals',
    LEADS: 'leads',
    CONTACTS: 'contacts',
    COMPANIES: 'companies',
    CALL_AUDITS: 'call_audits',
    ESCALATIONS: 'escalations',
    COURSES: 'courses',
    SETTINGS: 'settings'
};

/**
 * Default Object-level Access Rules
 */
export const DEFAULT_OBJECT_PERMISSIONS = {
    deals: { enabled: true, view: 'everything', edit: 'everything', delete: 'owned_only', create: true, export: true },
    leads: { enabled: true, view: 'everything', edit: 'everything', delete: 'owned_only', create: true, export: true },
    contacts: { enabled: true, view: 'everything', edit: 'everything', delete: 'none', create: true, export: true },
    companies: { enabled: true, view: 'everything', edit: 'everything', delete: 'none', create: true, export: true },
    deal_audits: { enabled: true, view: 'everything', edit: 'team_only', delete: 'none', create: true, export: false },
    call_audits: { enabled: true, view: 'everything', edit: 'team_only', delete: 'none', create: false, export: false },
    escalations: { enabled: true, view: 'everything', edit: 'everything', delete: 'team_only', create: true, export: false },
    courses: { enabled: true, view: 'everything', edit: 'none', delete: 'none', create: false, export: false },
    settings: { enabled: false, view: 'none', edit: 'none', delete: 'none', create: false, export: false }
};

/**
 * Default Property-level Access Rules (Field-Level Security)
 * 'edit': Can read and write
 * 'view': Can read, input locked/disabled
 * 'restricted': Completely hidden or masked with ••••••••
 */
export const DEFAULT_PROPERTY_PERMISSIONS = {
    deals: {
        amount: 'edit',
        gross_margin: 'view',
        commission_rate: 'restricted',
        discount_pct: 'view',
        stage: 'edit',
        deal_owner: 'edit',
        closedate: 'edit',
        probability: 'edit',
        payment_terms: 'view',
        billing_contact: 'edit'
    },
    leads: {
        phone: 'edit',
        email: 'edit',
        annual_revenue: 'view',
        lead_source: 'view',
        notes: 'edit',
        credit_score: 'restricted'
    },
    contacts: {
        phone: 'edit',
        email: 'edit',
        direct_line: 'view',
        personal_notes: 'edit'
    },
    companies: {
        annual_revenue: 'view',
        employee_count: 'view',
        tax_id: 'restricted'
    },
    call_audits: {
        score: 'view',
        transcript: 'view',
        audio_url: 'view',
        coaching_notes: 'edit',
        dispute_status: 'edit'
    },
    escalations: {
        severity: 'edit',
        resolution_notes: 'edit',
        internal_memo: 'restricted'
    }
};

/**
 * 1-Click Presets for Rapid Configuration
 */
export const ROLE_PERMISSION_PRESETS = {
    superadmin: {
        name: 'Super Admin / Executive',
        desc: 'Unrestricted full access across all objects, financial metrics, and settings.',
        badgeColor: 'bg-purple-100 text-purple-700 border-purple-200',
        objects: {
            deals: { enabled: true, view: 'everything', edit: 'everything', delete: 'everything', create: true, export: true },
            leads: { enabled: true, view: 'everything', edit: 'everything', delete: 'everything', create: true, export: true },
            contacts: { enabled: true, view: 'everything', edit: 'everything', delete: 'everything', create: true, export: true },
            companies: { enabled: true, view: 'everything', edit: 'everything', delete: 'everything', create: true, export: true },
            call_audits: { enabled: true, view: 'everything', edit: 'everything', delete: 'everything', create: true, export: true },
            escalations: { enabled: true, view: 'everything', edit: 'everything', delete: 'everything', create: true, export: true }
        },
        properties: {
            deals: { amount: 'edit', gross_margin: 'edit', commission_rate: 'edit', discount_pct: 'edit', stage: 'edit', deal_owner: 'edit', closedate: 'edit', probability: 'edit', payment_terms: 'edit', billing_contact: 'edit' },
            leads: { phone: 'edit', email: 'edit', annual_revenue: 'edit', lead_source: 'edit', notes: 'edit', credit_score: 'edit' },
            call_audits: { score: 'edit', transcript: 'edit', audio_url: 'edit', coaching_notes: 'edit', dispute_status: 'edit' }
        }
    },
    sales_rep: {
        name: 'Account Executive / Sales Rep',
        desc: 'Works owned deals & leads. Margins & commission rates hidden. Export restricted.',
        badgeColor: 'bg-blue-100 text-blue-700 border-blue-200',
        objects: {
            deals: { enabled: true, view: 'owned_only', edit: 'owned_only', delete: 'none', create: true, export: false },
            leads: { enabled: true, view: 'owned_only', edit: 'owned_only', delete: 'none', create: true, export: false },
            contacts: { enabled: true, view: 'everything', edit: 'owned_only', delete: 'none', create: true, export: false },
            companies: { enabled: true, view: 'everything', edit: 'owned_only', delete: 'none', create: true, export: false },
            call_audits: { enabled: true, view: 'owned_only', edit: 'none', delete: 'none', create: false, export: false },
            escalations: { enabled: true, view: 'owned_only', edit: 'owned_only', delete: 'none', create: true, export: false }
        },
        properties: {
            deals: { amount: 'edit', gross_margin: 'restricted', commission_rate: 'restricted', discount_pct: 'view', stage: 'edit', deal_owner: 'view', closedate: 'edit', probability: 'edit', payment_terms: 'view', billing_contact: 'edit' },
            leads: { phone: 'edit', email: 'edit', annual_revenue: 'view', lead_source: 'view', notes: 'edit', credit_score: 'restricted' },
            call_audits: { score: 'view', transcript: 'view', audio_url: 'view', coaching_notes: 'view', dispute_status: 'edit' }
        }
    },
    sdr_inbound: {
        name: 'SDR / Inbound Prospector',
        desc: 'Can view & edit leads, create deals. Financial margins and export completely blocked.',
        badgeColor: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        objects: {
            deals: { enabled: true, view: 'team_only', edit: 'none', delete: 'none', create: true, export: false },
            leads: { enabled: true, view: 'everything', edit: 'everything', delete: 'none', create: true, export: false },
            contacts: { enabled: true, view: 'everything', edit: 'everything', delete: 'none', create: true, export: false },
            companies: { enabled: true, view: 'everything', edit: 'view', delete: 'none', create: true, export: false },
            call_audits: { enabled: true, view: 'owned_only', edit: 'none', delete: 'none', create: false, export: false },
            escalations: { enabled: false, view: 'none', edit: 'none', delete: 'none', create: false, export: false }
        },
        properties: {
            deals: { amount: 'view', gross_margin: 'restricted', commission_rate: 'restricted', discount_pct: 'restricted', stage: 'view', deal_owner: 'view', closedate: 'view', probability: 'view', payment_terms: 'restricted', billing_contact: 'view' },
            leads: { phone: 'edit', email: 'edit', annual_revenue: 'view', lead_source: 'edit', notes: 'edit', credit_score: 'restricted' },
            call_audits: { score: 'view', transcript: 'view', audio_url: 'view', coaching_notes: 'view', dispute_status: 'restricted' }
        }
    },
    qa_auditor: {
        name: 'QA & Compliance Auditor',
        desc: 'Full view of all calls, audits, and pipeline data. Deal editing locked. Full audit scoring rights.',
        badgeColor: 'bg-amber-100 text-amber-700 border-amber-200',
        objects: {
            deals: { enabled: true, view: 'everything', edit: 'none', delete: 'none', create: false, export: true },
            leads: { enabled: true, view: 'everything', edit: 'none', delete: 'none', create: false, export: true },
            contacts: { enabled: true, view: 'everything', edit: 'none', delete: 'none', create: false, export: true },
            companies: { enabled: true, view: 'everything', edit: 'none', delete: 'none', create: false, export: true },
            call_audits: { enabled: true, view: 'everything', edit: 'everything', delete: 'none', create: true, export: true },
            escalations: { enabled: true, view: 'everything', edit: 'everything', delete: 'none', create: true, export: true }
        },
        properties: {
            deals: { amount: 'view', gross_margin: 'view', commission_rate: 'restricted', discount_pct: 'view', stage: 'view', deal_owner: 'view', closedate: 'view', probability: 'view', payment_terms: 'view', billing_contact: 'view' },
            leads: { phone: 'view', email: 'view', annual_revenue: 'view', lead_source: 'view', notes: 'view', credit_score: 'restricted' },
            call_audits: { score: 'edit', transcript: 'edit', audio_url: 'edit', coaching_notes: 'edit', dispute_status: 'edit' }
        }
    }
};

/**
 * Main hook to evaluate Object & Property Permissions
 */
export function usePermissions(customUser = null) {
    const { user: authUser, userRole } = useAuth();
    const { currentTenant } = useTenant();

    const activeUser = customUser || authUser;

    const permissions = useMemo(() => {
        // Superadmin bypass
        const emailLower = (activeUser?.email || '').toLowerCase();
        const isSuper = userRole === 'superadmin' || 
            ['sanjay@the2conf.com', 'isanjaydev@gmail.com', 'admin@oblique.com', 'business.eleventh@gmail.com'].some(e => emailLower.includes(e));

        if (isSuper) {
            return {
                isSuperAdmin: true,
                objects: ROLE_PERMISSION_PRESETS.superadmin.objects,
                properties: ROLE_PERMISSION_PRESETS.superadmin.properties
            };
        }

        // Check if user has explicit tenant permissions
        const userObjPerms = activeUser?.permissions?.objects || DEFAULT_OBJECT_PERMISSIONS;
        const userPropPerms = activeUser?.permissions?.properties || DEFAULT_PROPERTY_PERMISSIONS;

        return {
            isSuperAdmin: false,
            objects: userObjPerms,
            properties: userPropPerms
        };
    }, [activeUser, userRole]);

    /**
     * Check object-level permission
     * @param {string} objectKey - 'deals', 'leads', etc.
     * @param {'view'|'edit'|'create'|'delete'|'export'} action
     * @returns {boolean}
     */
    const canAccessObject = (objectKey, action = 'view') => {
        if (permissions.isSuperAdmin) return true;
        const obj = permissions.objects?.[objectKey];
        if (!obj || !obj.enabled) return false;

        if (action === 'create') return !!obj.create;
        if (action === 'export') return !!obj.export;

        const scope = obj[action];
        return scope && scope !== 'none';
    };

    /**
     * Check if user can access a specific record based on ownership
     */
    const canAccessRecord = (objectKey, record, action = 'view') => {
        if (permissions.isSuperAdmin) return true;
        const obj = permissions.objects?.[objectKey];
        if (!obj || !obj.enabled) return false;

        const scope = obj[action];
        if (!scope || scope === 'none') return false;
        if (scope === 'everything') return true;

        const userEmail = (activeUser?.email || '').toLowerCase();
        const userName = (activeUser?.primaryName || activeUser?.name || '').toLowerCase();
        const recordOwner = (record?.deal_owner || record?.owner || record?.createdBy || '').toLowerCase();

        if (scope === 'owned_only') {
            return recordOwner === userEmail || recordOwner === userName || record?.ownerId === activeUser?.id;
        }

        if (scope === 'team_only') {
            const userTeams = activeUser?.customTeams || [];
            const recordTeam = record?.team || record?.teamName;
            if (!recordTeam) return true;
            return userTeams.includes(recordTeam);
        }

        return true;
    };

    /**
     * Get property-level access tier: 'edit' | 'view' | 'restricted'
     */
    const getPropertyPermission = (objectKey, propertyKey) => {
        if (permissions.isSuperAdmin) return 'edit';
        const propScope = permissions.properties?.[objectKey]?.[propertyKey];
        return propScope || 'edit';
    };

    /**
     * Check if property is visible
     */
    const isPropertyVisible = (objectKey, propertyKey) => {
        return getPropertyPermission(objectKey, propertyKey) !== 'restricted';
    };

    /**
     * Check if property is editable
     */
    const isPropertyEditable = (objectKey, propertyKey) => {
        return getPropertyPermission(objectKey, propertyKey) === 'edit';
    };

    /**
     * Mask value if property is restricted
     */
    const maskPropertyValue = (objectKey, propertyKey, value) => {
        const perm = getPropertyPermission(objectKey, propertyKey);
        if (perm === 'restricted') return '••••••••';
        return value;
    };

    return {
        permissions,
        canAccessObject,
        canAccessRecord,
        getPropertyPermission,
        isPropertyVisible,
        isPropertyEditable,
        maskPropertyValue
    };
}
